import KYCClient from '@company-common/model/kyc-form/KYCClient'
import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import store from '../store/index'
import { routerRedirect } from '@core/logic/RouterRedirect'
import { clientFormLoader } from '@core/components/ClientFormWrapper'
import { userProfileRoutes } from './userProfile/index'
import { ClientManagementRoutes } from '@core/pages/clientManagement-v2/router'
import { CpdRoutes } from '@core/pages/cpd/CpdRoutes'
import { commissionModuleRouter } from '@core/pages/CommissionModule/route'
import { newBusinessRegisterRouter } from './new-business-register/NewBusinessRegisterRoutes'
import { FormsList } from '@core/model/recruit/FormsList'
import { scheduleRoutes } from './schedule/ScheduleRoute'
import { hierarchyRoutes } from '@/modules/hierarchySetup/router/index'

Vue.use(VueRouter)

// Wrapper for type check
function kycClientFormLoader (title: string, key: keyof KYCClient, componentPromise: Promise<typeof import('*.vue')>) {
  return clientFormLoader(title, key, componentPromise)
}

const routes: Array<RouteConfig> = [
  {
    path: '/',
    redirect: 'login'
  },
  {
    path: '/error',
    name: 'Error',
    component: () => import('@core/pages/error/ErrorPage.vue'),
    meta: {
      title: 'Error Page'
    }
  },
  {
    path: '/login/',
    name: 'Login',
    component: () => import('@core/pages/Login.vue'),
    meta: {
      title: 'Login'
    }
  },
  {
    path: '/reset/:authToken',
    name: 'Reset',
    component: () => import('@core/pages/Reset.vue'),
    meta: {
      title: 'Reset Password'
    }
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('@core/pages/Home.vue'),
    meta: {
      title: 'Home'
    }
  },
  {
    path: '/kyc-pdf/:kycPdfOid/',
    name: 'KYCPdf',
    component: () => import('../views/kyc-pdf/KycPdf.vue'),
    meta: {
      title: 'KYC Pdf'
    }
  },
  {
    path: '/kyc-form/:formOid/',
    name: 'KYCForm',
    redirect: 'kyc-form/:formOid/client-summary',
    component: () => import('../views/kyc-form/KYCForm.vue'),
    meta: {
      title: 'KYC Form'
    },
    children: [
      {
        path: 'client-summary',
        name: 'Client Summary',
        component: () => import('../views/kyc-form/section/ClientSummaryView.vue')
      },
      {
        path: 'important-notices',
        name: 'Important Notices',
        component: () => import('../views/kyc-form/section/ImportantNoticesView.vue')
      },
      // about you
      { path: 'about-you', redirect: 'about-you/personal-details' },
      {
        path: 'about-you/personal-details',
        name: 'Personal Details',
        component: () => kycClientFormLoader('Personal Details', 'personalDetails', import('../views/kyc-form/section/about-you/ThePersonalDetails.vue'))
      },
      {
        path: 'about-you/dependants',
        name: 'Dependants',
        component: () => import('../views/kyc-form/section/about-you/DependantsView.vue')
      },
      {
        path: 'about-you/trusted-individual',
        name: 'Trusted Individual',
        component: () => import('../views/kyc-form/section/about-you/ClientAccompanimentView.vue')
      },
      // investment risk profile
      { path: 'investment-risk-profile', redirect: 'investment-risk-profile/customer-knowledge-assessment' },
      {
        path: 'investment-risk-profile/customer-knowledge-assessment',
        name: 'Customer Knowledge Assessment',
        component: () => import('../views/kyc-form/section/investment-risk-profile/CustomerKnowledgeAssessmentView.vue')
      },
      {
        path: 'investment-risk-profile/risk-profile-assessment',
        name: 'Risk Profile Assessment',
        component: () => import('../views/kyc-form/section/investment-risk-profile/RiskProfileAssessmentView.vue')
      },
      // your finances
      { path: 'your-finances', redirect: 'your-finances/net-worth' },
      {
        path: 'your-finances/net-worth',
        name: 'Net Worth',
        component: () => import('../views/kyc-form/section/your-finances/NetWorthView.vue')
      },
      {
        path: 'your-finances/cash-flow',
        name: 'Cash Flow',
        component: () => import('../views/kyc-form/section/your-finances/CashFlowView.vue')
      },
      {
        path: 'your-finances/budget',
        name: 'Budget',
        component: () => import('../views/kyc-form/section/your-finances/BudgetView.vue')
      },
      { path: 'current-portfolio', redirect: 'current-portfolio/insurance' },
      {
        path: 'current-portfolio/insurance',
        name: 'Insurance Portfolio',
        component: () => import('../views/kyc-form/section/current-plans/insurance-portfolio/YourCurrentPlansPage.vue')
      },
      {
        path: 'current-portfolio/investment',
        name: 'Investment Portfolio',
        component: () => import('../views/kyc-form/section/current-plans/investment-portfolio/YourCurrentInvestPage.vue')
      },
      // Your Priorities & Needs Analysis
      { path: 'needs-analysis', redirect: 'needs-analysis/adjustments-and-assumptions' },
      {
        path: 'needs-analysis/identifying-your-priorities',
        name: 'Identifying Your Priorities',
        component: () => import('../views/kyc-form/section/needs-analysis/IdentifyingYourPrioritiesView.vue')
      },
      {
        path: 'needs-analysis/needs-analysis-calculation',
        name: 'Needs Analysis Calculation',
        component: () => import('../views/kyc-form/section/needs-analysis/NeedsAnalysisCalculation.vue')
      },
      {
        path: 'needs-analysis/adjustments-and-assumptions',
        name: 'Adjustments & Assumptions',
        component: () => import('../views/kyc-form/section/needs-analysis/AdjustmentsAndAssumptions.vue')
      },
      // Our Recommendations
      { path: 'our-recommendation', redirect: 'our-recommendation/product-recommendations' },
      {
        path: 'our-recommendation/product-recommendations',
        name: 'Product Recommendations',
        component: () => import('../views/kyc-form/section/recommendations/TheProductRecommendations.vue')
      },
      {
        path: 'our-recommendation/ilp-sub-funds',
        name: 'For Investments - ILP/Sub funds',
        component: () => import('../views/kyc-form/section/recommendations/TheILPSubfunds.vue')
      },
      {
        path: 'our-recommendation/basis-of-recommendation',
        name: 'Basis of Recommendation',
        component: () => import('../views/kyc-form/section/recommendations/TheBasisOfRecommendation.vue')
      },
      {
        path: 'our-recommendation/client-decision',
        name: 'Client\'s Decision',
        component: () => import('../views/kyc-form/section/recommendations/TheClientDecision.vue')
      },
      {
        path: 'replacement-or-switching',
        name: 'Replacement / Switching',
        component: () => import('../views/kyc-form/section/ReplacementOrSwitching.vue')
      },
      {
        path: 'disclaimers-and-client-checklist',
        name: 'Disclaimers & Client\'s Checklist',
        component: () => import('../views/kyc-form/section/DisclaimersAndClientChecklist.vue')
      },
      {
        path: 'client-declaration',
        name: 'Client\'s Declaration',
        component: () => import('../views/kyc-form/section/ClientDeclaration.vue')
      },
      {
        path: 'financial-consultant-declaration',
        name: 'Financial Adviser\'s Declaration',
        component: () => import('../views/kyc-form/section/FinancialConsultantDeclaration.vue')
      },
      {
        path: 'kyc-confirmation',
        name: 'KYC Confirmation',
        component: () => import('../views/kyc-form/section/kyc-confirmation/KYCConfirmation.vue')
      },
      {
        path: 'bop',
        name: 'Beneficial Owner Particulars Form',
        component: () => import('../views/kyc-form/section/BeneficialOwnerForm.vue')
      },
      {
        path: 'pep',
        name: 'Politically Exposed Person Form',
        component: () => import('../views/kyc-form/section/PEP.vue')
      },
      {
        path: 'aml',
        name: 'Client\'s AML Check',
        component: () => import('../views/kyc-form/section/AML.vue')
      },
      {
        path: 'appendix',
        name: 'Appendix - Upload PDFs',
        component: () => import('../views/kyc-form/section/UploadDocument.vue')
      }
    ]
  },
  {
    path: '/client-list/',
    name: 'client-list',
    component: () => import('@core/pages/ClientList.vue'),
    meta: {
      title: 'Client List'
    }
  },
  {
    path: '/client-management',
    name: 'client-management',
    component: () => import('@core/pages/client-management/views/v2/ClientProductListPage.vue')
  },
  {
    path: '/client-info/:clientOid/',
    name: 'client-info',
    redirect: '/client-info/:clientOid/profile',
    component: () => import('@core/pages/clientManagement-v2/views/ClientInfo.vue'),
    children: ClientManagementRoutes
  },
  {
    path: '/home-adviser/',
    name: 'home-adviser',
    component: () => import('../views/HomeAdviser.vue'),
    meta: {
      title: 'Home Adviser'
    }
  },
  {
    path: '/home-submission/',
    name: 'home-submission',
    component: () => import('@core/pages/HomeSubmission.vue'),
    meta: {
      title: 'Home Submission'
    }
  },
  {
    path: '/home-supervisor/',
    name: 'home-supervisor',
    component: () => import('@core/pages/HomeSupervisor.vue'),
    meta: {
      title: 'Home Supervisor'
    }
  },
  {
    path: '/bsc-home/',
    name: 'bsc-home',
    component: () => import('@core/pages/bsc/BscHome.vue')
  },
  {
    path: '/bsc-list-new/:reviewOid',
    name: 'bsc-list-new',
    component: () => import('@core/pages/bsc/BscListNew.vue')
  },
  {
    path: '/user-bsc-record/:userOid/:bscOid',
    name: 'User Bsc Record',
    component: () => import('@core/pages/bsc/UserBscRecord.vue')
  },
  {
    path: '/bsc-policy-review',
    name: 'bsc-policy-review',
    component: () => import('@core/pages/bsc/BscPolicyReview.vue')
  },
  {
    path: '/company-codes-list',
    name: 'company-codes-list',
    component: () => import('@core/pages/companyDetails/CompanyCodesList.vue')
  },
  {
    path: '/user-list',
    name: 'user-list',
    component: () => import('@core/pages/account/UserListV2.vue')
  },
  {
    path: '/group-list',
    name: 'group-list',
    component: () => import('@core/pages/account/GroupList.vue')
  },
  {
    path: '/group-profile/:groupOid',
    name: 'group-profile',
    component: () => import('@core/pages/account/GroupProfile.vue')
  },
  {
    path: '/retrieve_myinfo',
    name: 'retrieve-my-info',
    component: () => import('../views/myinfo/TheMyInfoView.vue')
  },
  {
    path: '/commission-home',
    name: 'commission-home',
    component: () => import('@core/pages/CommissionModule/CommissionModule.vue')
  },
  {
    path: '/commission-details/:_oid?',
    name: 'commission-details',
    component: () => import('@core/pages/CommissionModule/CommissionDetails.vue')
  },
  {
    path: '/change-log',
    name: 'change-log',
    component: () => import('@core/pages/change-log/Changelog.vue'),
    meta: {
      title: 'Change Log'
    }
  },
  {
    path: '/admin-pdfUpload',
    name: 'admin-pdfUpload',
    component: () => import('@core/pages/admin-pdfUpload/AdminPdfUpload.vue')
  },
  {
    path: '/admin-products',
    name: 'Admin Product List',
    component: () => import('@core/pages/adminProductList/AdminProductList.vue'),
    meta: {
      title: 'Admin Products'
    }
  },
  {
    path: '/agent-codes-mass-update',
    name: 'agent-codes-mass-update',
    component: () => import('@core/pages/account/AgentCodesMassUpdate.vue'),
    meta: {
      title: 'Agent Code Mass Update'
    }
  },
  {
    path: '/admin-assumptions',
    name: 'Global Assumptions',
    component: () => import('@core/pages/assumptions/AssumptionsAdmin.vue'),
    meta: {
      title: 'Global Assumptions'
    }
  },
  {
    path: '/payee-list',
    name: 'Payee List',
    component: () => import('@core/pages/intermediary/Payee.vue'),
    meta: {
      title: 'Intermediary List'
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@core/pages/Admin.vue'),
    meta: {
      title: 'Admin'
    }
  },
  {
    path: '/resources',
    name: 'Resource Management System',
    component: () => import('@core/pages/resources/Resources.vue'),
    meta: {
      title: 'Resources Management System'
    }
  },
  {
    path: '/report-management',
    name: 'Report Dashboard',
    component: () => import('@core/pages/report-management/ReportManagement.vue'),
    meta: {
      title: 'Report Management Dashboard'
    }
  },
  ...newBusinessRegisterRouter,
  ...scheduleRoutes,
  {
    path: '/home-recruiter/',
    name: 'home-recruiter',
    component: () => import('@core/pages/HomeRecruiter.vue'),
    meta: {
      title: 'Home Supervisor'
    }
  },
  {
    path: '/recruit/:recruitAccountOid',
    name: 'recruit',
    component: () => import('@core/pages/activity/Board.vue'),
    meta: {
      title: 'Recruit Activity'
    }
  },
  {
    path: '/recruit/:recruitAccountOid/forms',
    name: 'recruit-forms',
    redirect: `/recruit/:recruitAccountOid/forms/${FormsList[0]?.path}`,
    component: () => import('@core/pages/recruit/RecruitForms.vue'),
    children: FormsList
  },
  {
    path: '/sales-team-administration/',
    name: 'Sales Team Administration',
    component: () => import('@core/pages/sales-team-administration/Landing.vue')
  },
  ...hierarchyRoutes,
  ...commissionModuleRouter,
  ...CpdRoutes,
  userProfileRoutes(),
  {
    path: '/disign-panel',
    name: 'Disign Panel',
    component: () => import('@core/pages/disign/DisignHome.vue')
  },
  {
    path: '*',
    component: () => import('@core/pages/error/NotFoundPage.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  routes,
  base: '/live'
})

const publicPage = ['Login', 'Reset', 'AdminLogin', 'retrieve-my-info', 'Error']

// Before
router.beforeEach(routerRedirect(store, publicPage))

export default router
